'use client'

import { Settings, Users, Shield, Bell } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { TeamConfigurationPanel } from './TeamConfigurationPanel'

interface ConfigurationSection {
  id: string
  title: string
  description: string
  icon: LucideIcon
}

const CONFIGURATION_SECTIONS: ConfigurationSection[] = [
  {
    id: 'teams',
    title: 'Teams',
    description: 'Manage team members and configurations',
    icon: Users,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Configure notification preferences',
    icon: Bell,
  },
  {
    id: 'security',
    title: 'Security',
    description: 'Security and access control settings',
    icon: Shield,
  },
  {
    id: 'general',
    title: 'General',
    description: 'General configuration settings',
    icon: Settings,
  },
]

interface AgentConfigurationPanelProps {
  className?: string
}

export function AgentConfigurationPanel({
  className = '',
}: AgentConfigurationPanelProps) {
  const [activeConfigId, setActiveConfigId] = useState('teams')

  const activeSection = CONFIGURATION_SECTIONS.find(
    section => section.id === activeConfigId
  )

  return (
    <div className={`flex flex-col gap-6 p-6 lg:flex-row ${className}`}>
      {/* Configuration Sidebar */}
      <aside className="w-full space-y-4 lg:w-72">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Configuration
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Manage system settings and team configurations.
          </p>
        </div>

        <div className="space-y-2">
          {CONFIGURATION_SECTIONS.map(section => {
            const isActive = section.id === activeConfigId
            const Icon = section.icon

            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveConfigId(section.id)}
                className={`w-full text-left rounded-lg border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  isActive
                    ? 'border-blue-200 bg-blue-50 text-blue-700 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex-shrink-0 ${
                      isActive ? 'text-blue-600' : 'text-gray-400'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      className={`text-sm font-medium ${
                        isActive ? 'text-blue-700' : 'text-gray-900'
                      }`}
                    >
                      {section.title}
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        isActive ? 'text-blue-600' : 'text-gray-500'
                      }`}
                    >
                      {section.description}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 min-w-0">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-3">
              {activeSection && (
                <>
                  <span className="text-blue-600">
                    <activeSection.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {activeSection.title}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {activeSection.description}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-6">
            {activeConfigId === 'teams' && <TeamConfigurationPanel />}
            {activeConfigId === 'notifications' && (
              <div className="text-center py-12">
                <Bell className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Notifications Configuration
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Notification settings coming soon.
                </p>
              </div>
            )}
            {activeConfigId === 'security' && (
              <div className="text-center py-12">
                <Shield className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Security Configuration
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Security settings coming soon.
                </p>
              </div>
            )}
            {activeConfigId === 'general' && (
              <div className="text-center py-12">
                <Settings className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  General Configuration
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  General settings coming soon.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
